import { useRef, useEffect, useState } from 'react';
import { Box, IconButton, TextareaAutosize } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AttachFileIcon from '@mui/icons-material/AttachFile';

export default function MessageInput({ onSend }) {
  const [text, setText] = useState('');
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [draftVoiceText, setDraftVoiceText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    el.style.height = 'auto';

    const maxHeight = 200;
    const newHeight = Math.min(el.scrollHeight, maxHeight);

    el.style.height = `${newHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [text, draftVoiceText, listening]);

  function formatUserInput(text) {
    if (!text) return '';

    let formatted = text.trim();

    // Normalize multiple spaces
    formatted = formatted.replace(/\s+/g, ' ');

    // Break into paragraphs for common speech patterns
    formatted = formatted
      .replace(/(\.|!|\?)\s+/g, '$1\n\n') // sentence breaks
      .replace(/(\d+\.|\bfirst\b|\bsecond\b|\bthird\b|\bfourth\b|\bfifth\b)/gi, '\n$1 ')
      .replace(/\n{3,}/g, '\n\n'); // max 2 line breaks

    return formatted.trim();
  }

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    let finalTranscriptRef = '';
    recognition.onresult = (event) => {
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const t = result[0].transcript;

        if (result.isFinal) {
          finalTranscriptRef += t + ' ';
        } else {
          interimTranscript += t;
        }
      }

      // show live text (final + interim), but don't "store" interim forever
      setDraftVoiceText((finalTranscriptRef + interimTranscript).trim());
    };

    recognition.onend = () => {
      finalTranscriptRef = '';
    };

    recognitionRef.current = recognition;
  }, []);

  const confirmVoice = () => {
    setListening(false);

    setText((prev) => {
      if (!draftVoiceText.trim()) return prev;
      return prev ? `${prev} ${draftVoiceText.trim()}` : draftVoiceText.trim();
    });

    setDraftVoiceText('');

    setTimeout(() => {
      recognitionRef.current?.stop();
    }, 0);
  };

  const cancelVoice = () => {
    setListening(false); // UI first
    setDraftVoiceText('');

    setTimeout(() => {
      recognitionRef.current?.stop();
    }, 0);
  };

  const toggleMic = () => {
    if (!recognitionRef.current) return;

    if (listening) {
      recognitionRef.current.stop();
      setListening(false);
    } else {
      recognitionRef.current.start();
      setListening(true);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setAttachments((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleSend = () => {
    if (!text.trim() && attachments.length === 0) return;
    if (typeof onSend !== 'function') return;
    const formatted = formatUserInput(text);
    onSend({
      text: formatted,
      attachments
    });
    setText('');
    setAttachments([]);
  };

  return (
    <Box p={2} borderTop="1px solid #eee">
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: '16px',
          p: 1.5,
          backgroundColor: '#fff',
          maxWidth: 880,
          mx: 'auto'
        }}
      >
        {/* 🔹 ATTACHMENTS ROW (TOP — GPT STYLE) */}
        {attachments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {attachments.map((file, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: '#f4f4f5',
                  fontSize: 13
                }}
              >
                {file.name}
                <CloseIcon
                  sx={{ ml: 1, fontSize: 14, cursor: 'pointer' }}
                  onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* 🔹 INPUT ROW */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <input ref={fileInputRef} type="file" hidden multiple onChange={handleFileSelect} />

          {!listening && (
            <IconButton onClick={() => fileInputRef.current?.click()}>
              <AttachFileIcon />
            </IconButton>
          )}

          <TextareaAutosize
            ref={textareaRef}
            value={listening ? draftVoiceText : text}
            placeholder="Ask Phoenix AI..."
            minRows={1}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (typeof onSend === 'function') {
                  handleSend();
                }
              }
            }}
            style={{
              flex: 1,
              width: '100%',
              resize: 'none',
              fontSize: '0.98rem',
              lineHeight: 1.8,
              padding: '14px 16px',
              borderRadius: '16px',
              border: '1px solid #e0e0e0',
              outline: 'none',
              maxHeight: '220px',
              overflowY: 'auto',
              fontFamily: 'inherit',
              backgroundColor: '#fff',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
            }}
          />

          {!listening ? (
            <>
              <IconButton onClick={toggleMic} color="primary">
                <MicIcon />
              </IconButton>

              <IconButton disabled={!text.trim() && attachments.length === 0} onClick={handleSend} color="primary">
                <SendIcon />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton onClick={cancelVoice}>
                <CloseIcon />
              </IconButton>

              <IconButton onClick={confirmVoice} color="primary">
                <CheckIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
